import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchStorages, ProfileStorageLocation} from 'api/documents/create';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {boundUserWithParticipantFx, createUserFx} from 'models/profile/staff/table';
import {EmployeeRegistrationRequest} from 'types';
import {ProfileType} from 'types/profile';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type FormField = {
    name: keyof EmployeeRegistrationRequest;
    readonly?: boolean;
    type?: 'text' | 'password';
    options?: SelectOption<string>[];
};

type CreateUserForm = EmployeeRegistrationRequest & {
    storageLocationId?: number | null;
};

export const CreateUser = () => {
    const {t, i18n} = useTranslation();
    const permissions = useStore(permissions$);
    const [storageLocations, setStorageLocations] = useState<ProfileStorageLocation[]>([]);
    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.register.validations.${key}`, replace),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .required(translateValidates('required')),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .required(translateValidates('required'))
                    .typeError(translateValidates('required'))
                    .matches(/^\+?\d{10,14}$/, translateValidates('phone')),
                storageLocationId: yup
                    .number()
                    .nullable()
                    .notRequired(),
                type: yup.mixed<ProfileType.EMPLOYEE>().notRequired(),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setError,
        reset,
    } = useForm<CreateUserForm>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    useEffect(() => {
        fetchStorages()
            .then((response) => setStorageLocations(response.storageLocations))
            .catch((error) => console.error(error));
    }, []);

    const storageLocationOptions = useMemo<SelectOption<number>[]>(
        () =>
            storageLocations.map((item) => ({
                value: item.id,
                label: item.storageName ?? '-',
                isDisabled: item.status === 'deactivated',
            })),
        [storageLocations],
    );

    const canCreateUser =
        permissions.includes(permissions.userOwnRegister) ||
        permissions.includes(permissions.userAllRegister);

    const onSubmit = async (data: CreateUserForm) => {
        try {
            if (!canCreateUser) return;

            const payload: EmployeeRegistrationRequest = {
                email: data.email,
                // password: data.password,
                type: ProfileType.EMPLOYEE,
                first_name: data.first_name,
                last_name: data.last_name,
                ...(data.middle_name && {middle_name: data.middle_name}),
                ...(data.phone && {phone: data.phone}),
            };

            const response = await createUserFx(payload);
            if (data.storageLocationId && response.data) {
                await boundUserWithParticipantFx({
                    userId: response.data,
                    clientId: data.storageLocationId,
                });
            }
            reset();
            history.push(ROUTE.STAFF);
        } catch (error) {
            console.error(error);
            const responseData = error.fullErrorsList;
            if (!Array.isArray(responseData)) return;

            responseData.forEach(({field, message}) => setError(field, {type: 'manual', message}));
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'first_name'},
            {name: 'middle_name'},
            {name: 'last_name'},
            {name: 'email'},
            // {name: 'password', type: 'password'},
            // {name: 'password_confirm', type: 'password'},
            {name: 'phone'},
        ],
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<CreateUserForm>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
            ...(field.type && {type: field.type}),
            autoComplete: 'new-password',
        };

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.STAFF}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit)}
                        style={{marginTop: 50}}
                    >
                        <CustomTitle size={4}>{t('pages.profile.staff.create.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                            <Field>
                                <span>{t('pages.profile.staff.storageLocation')}</span>
                                <Controller
                                    name="storageLocationId"
                                    control={control}
                                    defaultValue={null}
                                    render={({value, onChange}) => (
                                        <Select
                                            isClearable
                                            value={
                                                storageLocationOptions.find(
                                                    (option) => option.value === value,
                                                ) ?? null
                                            }
                                            options={storageLocationOptions}
                                            onChange={(option: SelectOption<number> | null) =>
                                                onChange(option?.value ?? null)
                                            }
                                        />
                                    )}
                                />
                            </Field>
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                size="sm"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit" size="sm" disabled={!canCreateUser}>
                                {t('pages.profile.staff.create.createBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
